/**
 * Google Analytics 4 Integration
 * Handles GA4 initialization and event tracking
 */

import { GA4_MEASUREMENT_ID } from '../../config/config.js';
import { getUTMData, isDevelopment, waitForValue } from './utils.js';

let initialized = false;
let gaClientId = null;

/**
 * Initialize Google Analytics 4
 */
export function initGA4() {
  // Skip if already loaded or no measurement ID
  if (window.gtag || initialized) return;

  if (!GA4_MEASUREMENT_ID || GA4_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    console.warn('[GA4] No measurement ID configured - skipping initialization');
    return;
  }

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: true,
    // Add 'app' property to distinguish from ShowLark landing page events
    custom_map: {
      'dimension1': 'app'
    }
  });

  // Set app property on all events
  window.gtag('set', { 'app': 'visualizer' });

  // Try to capture client ID
  captureClientId();

  initialized = true;
  if (isDevelopment()) console.log('[GA4] Initialized');
}

/**
 * Capture GA client ID for linking with other analytics.
 * Polls via gtag('get') until the client ID is available.
 */
function captureClientId() {
  if (!window.gtag) return;

  // Poll for client ID instead of a fixed 1-second delay
  waitForValue(
    () => new Promise((resolve) => {
      try {
        let settled = false;
        window.gtag('get', GA4_MEASUREMENT_ID, 'client_id', (id) => {
          if (!settled) { settled = true; resolve(id || null); }
        });
        // Safety timeout — if gtag never calls back, resolve null to retry
        setTimeout(() => { if (!settled) { settled = true; resolve(null); } }, 500);
      } catch {
        resolve(null);
      }
    }),
    { maxAttempts: 10, intervalMs: 300 }
  ).then((clientId) => {
    if (clientId) {
      gaClientId = clientId;
      if (isDevelopment()) console.log('[GA4] Client ID captured');
    } else {
      console.warn('[GA4] Could not capture client ID after polling');
    }
  });
}

/**
 * Get the GA client ID
 * @returns {string|null}
 */
export function getGAClientId() {
  return gaClientId;
}

/**
 * Track a custom event to GA4
 * @param {string} eventName - Name of the event
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!window.gtag) {
    if (isDevelopment()) {
      console.log('[GA4] (dev) Event:', eventName, params);
    }
    return;
  }

  const utmData = getUTMData();

  const eventParams = {
    ...params,
    ...utmData,
    app: 'visualizer' // Always include app identifier
  };

  window.gtag('event', eventName, eventParams);

  if (isDevelopment()) console.log('[GA4] Event:', eventName, eventParams);
}

/**
 * Track page view
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export function trackPageView(pagePath, pageTitle) {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
}

/**
 * Set user properties
 * @param {Object} properties - User properties to set
 */
export function setUserProperties(properties) {
  if (!window.gtag) return;

  window.gtag('set', 'user_properties', {
    ...properties,
    app: 'visualizer'
  });

  if (isDevelopment()) console.log('[GA4] User properties set:', properties);
}

/**
 * Set user ID for cross-device tracking
 * @param {string} userId - User ID
 */
export function setUserId(userId) {
  if (!window.gtag) return;

  window.gtag('set', { 'user_id': userId });
  if (isDevelopment()) console.log('[GA4] User ID set');
}

/**
 * Check if GA4 is initialized
 * @returns {boolean}
 */
export function isInitialized() {
  return initialized && !!window.gtag;
}
